import React, { useEffect, useState, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import MarketingShell from "./MarketingShell";
import Seo from "./Seo";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Heart, ArrowRight, CheckCircle2, Sparkles, Lock, Globe2 } from "lucide-react";

/**
 * Post-payment landing for the marketing /donate flow.
 *
 * Lifecycle:
 *   1. Land at /donate/success?session_id=cs_live_...
 *   2. Poll GET /api/donations/status/<id> until payment_status === "paid"
 *      (Stripe webhooks usually update us within 1-3s; we poll up to ~30s).
 *   3. Show a thank-you + dedication form.
 *   4. On submit → PATCH /api/donations/<id>/dedication, then offer a path
 *      to the Quiet Wall.
 *
 * Survivors who don't want to dedicate just skip — privacy is the default.
 */

const POLL_MS = 1500;
const POLL_MAX_ATTEMPTS = 20; // ~30s total

export default function DonateSuccess() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(search);
  const sessionId = params.get("session_id");
  // dt = dedication_token issued by the backend at checkout time. Required
  // when the donor crossed environments (iOS app → Stripe → Safari) because
  // their anonymous device id no longer matches the original transaction.
  const dedicationToken = params.get("dt");

  const [status, setStatus] = useState("loading"); // loading | paid | pending | error | missing
  const [tx, setTx] = useState(null);
  const [attempts, setAttempts] = useState(0);

  // Dedication form state
  const [showForm, setShowForm] = useState(false);
  const [honoree, setHonoree] = useState("");
  const [note, setNote] = useState("");
  const [donorLabel, setDonorLabel] = useState("");
  const [sharePublicly, setSharePublicly] = useState(true);
  // Optional email field — only used when the transaction hasn't been sent
  // a receipt yet (Stripe didn't capture one at checkout). Survivors who
  // want a tax-record receipt can drop it here.
  const [receiptEmail, setReceiptEmail] = useState("");
  const [savingDedication, setSavingDedication] = useState(false);
  const [savedToWall, setSavedToWall] = useState(false);

  const poll = useCallback(async () => {
    if (!sessionId) { setStatus("missing"); return; }
    try {
      const qs = dedicationToken ? `?dt=${encodeURIComponent(dedicationToken)}` : "";
      const { data } = await api.get(`/donations/status/${sessionId}${qs}`);
      setTx(data);
      if (data.payment_status === "paid") {
        setStatus("paid");
        // Pre-fill any existing honor info (e.g. if user already dedicated
        // pre-payment in the in-app flow).
        if (data.honoree_name) setHonoree(data.honoree_name);
        if (data.dedication_note) setNote(data.dedication_note);
        if (data.share_publicly) setSharePublicly(true);
      } else {
        // Keep polling for a bit — Stripe webhook usually arrives in seconds.
        setAttempts((n) => n + 1);
      }
    } catch (e) {
      const code = e?.response?.status;
      if (code === 404) {
        setStatus("missing");
      } else {
        setStatus("error");
      }
    }
  }, [sessionId, dedicationToken]);

  // Single effect handles the polling loop. Re-runs when sessionId changes
  // (initial mount) or when attempts increments (each retry). Using a
  // single effect avoids the "set-state-in-effect" anti-pattern.
  useEffect(() => {
    if (!sessionId) { setStatus("missing"); return; }
    if (status === "paid" || status === "missing" || status === "error" || status === "pending") return;
    if (attempts >= POLL_MAX_ATTEMPTS) { setStatus("pending"); return; }
    const t = setTimeout(poll, attempts === 0 ? 0 : POLL_MS);
    return () => clearTimeout(t);
  }, [sessionId, attempts, status, poll]);

  const saveDedication = async () => {
    if (!honoree.trim()) {
      toast.info("Add an honoree name to save to the wall (or skip — your gift is already received).");
      return;
    }
    setSavingDedication(true);
    try {
      const qs = dedicationToken ? `?dt=${encodeURIComponent(dedicationToken)}` : "";
      await api.patch(`/donations/${sessionId}/dedication${qs}`, {
        honoree_name: honoree.trim().slice(0, 80),
        dedication_note: note.trim().slice(0, 280) || null,
        donor_label: donorLabel.trim().slice(0, 40) || null,
        share_publicly: sharePublicly,
        email: receiptEmail.trim() || undefined,
      });
      setSavedToWall(sharePublicly);
      toast.success(sharePublicly ? "Saved to the Quiet Wall." : "Saved — kept between us.");
      setShowForm(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save dedication. Please try again.");
    } finally {
      setSavingDedication(false);
    }
  };

  return (
    <MarketingShell>
      <Seo title="Thank you" description="Your donation supports survivors of intimate partner abuse. Sister Signal is built from lived experience." path="/donate/success" />

      <article className="max-w-2xl mx-auto px-6 sm:px-10 py-20" data-testid="page-donate-success">

        {/* MISSING / ERROR STATES ------------------------------------------ */}
        {status === "missing" && (
          <section className="rounded-3xl bg-card border border-border p-8 text-center">
            <p className="font-serif text-3xl">We couldn&rsquo;t find that donation.</p>
            <p className="mt-3 text-foreground/70">If you just gave, please check your email for a Stripe receipt — your gift went through.</p>
            <Link to="/donate" className="mt-6 inline-flex items-center gap-1 text-brand hover:underline">Try again <ArrowRight className="w-4 h-4" /></Link>
          </section>
        )}

        {status === "error" && (
          <section className="rounded-3xl bg-card border border-border p-8 text-center">
            <p className="font-serif text-3xl">Something went sideways.</p>
            <p className="mt-3 text-foreground/70">Your payment likely succeeded — Stripe will email you a receipt. Refresh in a moment if you&rsquo;d like to add a dedication.</p>
            <button onClick={() => { setStatus("loading"); setAttempts(0); }} className="mt-6 inline-flex items-center gap-1 text-brand hover:underline">
              Try again <ArrowRight className="w-4 h-4" />
            </button>
          </section>
        )}

        {/* LOADING — payment confirmation pending --------------------------- */}
        {status === "loading" && (
          <section className="rounded-3xl bg-card border border-border p-8 text-center" data-testid="donate-success-loading">
            <Sparkles className="w-10 h-10 text-brand mx-auto animate-soft-pulse" />
            <p className="mt-4 font-serif text-3xl">Confirming your gift…</p>
            <p className="mt-3 text-foreground/70 leading-relaxed">
              We&rsquo;re waiting on Stripe to confirm. This usually takes a few seconds.
            </p>
            <p className="mt-1 text-xs text-foreground/45">Attempt {attempts + 1}</p>
          </section>
        )}

        {/* PENDING — webhook hasn't arrived after ~30s --------------------- */}
        {status === "pending" && (
          <section className="rounded-3xl bg-card border border-brand/20 p-8" data-testid="donate-success-pending">
            <p className="font-script text-2xl text-brand">in transit</p>
            <h1 className="font-serif text-4xl leading-tight mt-1">Your gift is on its way.</h1>
            <p className="mt-4 text-foreground/75 leading-relaxed">
              Stripe is taking a moment to confirm. You should receive a receipt by email within a few minutes.
              Refresh this page once it arrives to add a dedication to the Quiet Wall.
            </p>
            <button
              onClick={() => { setStatus("loading"); setAttempts(0); }}
              data-testid="donate-success-recheck"
              className="mt-6 inline-flex items-center gap-1 px-5 py-2.5 rounded-full bg-brand text-white text-sm hover:bg-brand/90 transition-all"
            >
              Check again <ArrowRight className="w-4 h-4" />
            </button>
          </section>
        )}

        {/* PAID — thank you + optional dedication --------------------------- */}
        {status === "paid" && (
          <>
            <section className="rounded-3xl bg-brand-soft/40 border border-brand/20 p-8 text-center" data-testid="donate-success-paid">
              <CheckCircle2 className="w-12 h-12 text-brand mx-auto" />
              <p className="font-script text-2xl text-brand mt-4">received with care</p>
              <h1 className="font-serif text-4xl sm:text-5xl leading-tight mt-1">Thank you.</h1>
              {tx?.amount != null && (
                <p className="mt-4 text-lg text-foreground/80">
                  Your gift of <strong>${Number(tx.amount).toFixed(2)}</strong>
                  {tx.mode === "monthly" ? " monthly" : ""} helps keep Sister Signal free for every survivor who needs it.
                </p>
              )}
              <p className="mt-3 text-foreground/70 leading-relaxed">
                A receipt is on its way to your email. If you&rsquo;d like to dedicate this gift to someone — quietly, or on the Quiet Wall — you can do that below.
              </p>
            </section>

            {/* Already dedicated (from pre-payment in-app flow) ----------- */}
            {tx?.honoree_name && !showForm && !savedToWall && (
              <section className="mt-6 rounded-3xl bg-card border border-border p-7" data-testid="dedication-existing">
                <p className="font-script text-xl text-brand">your dedication</p>
                <p className="mt-2 font-serif text-2xl italic leading-snug">In honor of {tx.honoree_name}</p>
                {tx.dedication_note && (
                  <p className="mt-3 font-serif italic text-foreground/85 leading-relaxed">&ldquo;{tx.dedication_note}&rdquo;</p>
                )}
                <p className="mt-3 text-sm text-foreground/55">
                  {tx.share_publicly
                    ? "Shared on the Quiet Wall."
                    : "Kept private. Only you can see it."}
                </p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <button
                    onClick={() => setShowForm(true)}
                    data-testid="dedication-edit"
                    className="text-sm text-brand hover:underline"
                  >
                    Edit dedication
                  </button>
                  {tx.share_publicly && (
                    <Link to="/wall" className="text-sm text-brand hover:underline">
                      Visit the Quiet Wall →
                    </Link>
                  )}
                </div>
              </section>
            )}

            {/* DEDICATION FORM ------------------------------------------ */}
            {!savedToWall && (showForm || !tx?.honoree_name) && (
              <section className="mt-6 rounded-3xl bg-card border border-border p-7" data-testid="dedication-form">
                <p className="font-script text-xl text-brand">if you&rsquo;d like</p>
                <h2 className="mt-1 font-serif text-2xl leading-snug">Would you like to dedicate this gift?</h2>
                <p className="mt-2 text-sm text-foreground/65 leading-relaxed">
                  Optional. We never publish amounts — only the name and note you choose to share.
                </p>

                <div className="mt-5 space-y-4">
                  <Field label="In honor of">
                    <input
                      data-testid="dedication-honoree"
                      value={honoree}
                      onChange={(e) => setHonoree(e.target.value)}
                      placeholder="A name, a relationship, or 'the woman I used to be'"
                      maxLength={80}
                      className="w-full h-12 rounded-2xl bg-background border border-border px-4"
                    />
                  </Field>
                  <Field label="A note (optional)">
                    <textarea
                      data-testid="dedication-note"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Something gentle. The wall is read by people who are still in it."
                      maxLength={280}
                      rows={4}
                      className="w-full rounded-2xl bg-background border border-border px-4 py-3 leading-relaxed resize-none"
                    />
                    <p className="mt-1 text-xs text-foreground/45 text-right">{note.length}/280</p>
                  </Field>
                  <Field label="Sign as (optional)">
                    <input
                      data-testid="dedication-label"
                      value={donorLabel}
                      onChange={(e) => setDonorLabel(e.target.value)}
                      placeholder="e.g. 'from her sister'"
                      maxLength={40}
                      className="w-full h-12 rounded-2xl bg-background border border-border px-4"
                    />
                  </Field>

                  {/* Receipt email — only surfaced when Stripe didn't capture
                      one. Once a receipt has been sent (receipt_sent_at on
                      the tx), we hide this so we don't spam the donor. */}
                  {!tx?.receipt_sent_at && (
                    <Field label="Email for tax receipt (optional)">
                      <input
                        data-testid="dedication-receipt-email"
                        type="email"
                        value={receiptEmail}
                        onChange={(e) => setReceiptEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="w-full h-12 rounded-2xl bg-background border border-border px-4"
                      />
                      <p className="mt-1 text-xs text-foreground/55">
                        We&rsquo;ll send a Sister Signal receipt with tax-record details. Skip to keep it offline.
                      </p>
                    </Field>
                  )}

                  {/* Public / Private toggle */}
                  <div className="rounded-2xl bg-brand-soft/30 border border-brand/15 p-5" data-testid="dedication-visibility">
                    <p className="text-xs uppercase tracking-widest text-foreground/55">Visibility</p>
                    <div className="mt-2 flex flex-col sm:flex-row gap-2">
                      <button
                        type="button"
                        onClick={() => setSharePublicly(true)}
                        data-testid="dedication-visibility-public"
                        className={`flex-1 rounded-xl p-3 text-left border transition-all ${
                          sharePublicly ? "border-brand bg-card" : "border-border bg-card/50 hover:border-brand/30"
                        }`}
                      >
                        <div className="flex items-center gap-2 font-serif">
                          <Globe2 className="w-4 h-4 text-brand" /> Share on the Quiet Wall
                        </div>
                        <p className="mt-1 text-xs text-foreground/65">Visible to anyone who visits sistersignal.org/wall.</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setSharePublicly(false)}
                        data-testid="dedication-visibility-private"
                        className={`flex-1 rounded-xl p-3 text-left border transition-all ${
                          !sharePublicly ? "border-brand bg-card" : "border-border bg-card/50 hover:border-brand/30"
                        }`}
                      >
                        <div className="flex items-center gap-2 font-serif">
                          <Lock className="w-4 h-4 text-brand" /> Keep this private
                        </div>
                        <p className="mt-1 text-xs text-foreground/65">Only you ever see it. Your gift still counts.</p>
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-3 pt-2">
                    <button
                      onClick={saveDedication}
                      disabled={savingDedication}
                      data-testid="dedication-save"
                      className="px-6 py-3 rounded-full bg-brand text-white hover:bg-brand/90 transition-all disabled:opacity-60 inline-flex items-center gap-2"
                    >
                      <Heart className="w-4 h-4" />
                      {savingDedication ? "Saving…" : "Save dedication"}
                    </button>
                    <button
                      onClick={() => navigate("/")}
                      data-testid="dedication-skip"
                      className="text-sm text-foreground/55 hover:text-brand"
                    >
                      Skip — return home
                    </button>
                  </div>
                </div>
              </section>
            )}

            {/* SAVED — onward ------------------------------------------- */}
            {savedToWall && (
              <section className="mt-6 rounded-3xl bg-card border border-brand/20 p-7 text-center" data-testid="dedication-saved">
                <Heart className="w-8 h-8 text-brand mx-auto" />
                <p className="mt-3 font-serif text-2xl">Saved to the Quiet Wall.</p>
                <p className="mt-2 text-foreground/70">It will appear among the others — quietly, without a count beside it.</p>
                <div className="mt-5 flex flex-wrap justify-center gap-3">
                  <Link to="/wall" data-testid="dedication-visit-wall"
                    className="inline-flex items-center gap-1 px-6 py-3 rounded-full bg-brand text-white hover:bg-brand/90 transition-all">
                    Visit the Quiet Wall <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/" className="inline-flex items-center gap-1 px-6 py-3 rounded-full bg-card border border-border hover:border-brand/40 transition-all">
                    Return home
                  </Link>
                </div>
              </section>
            )}
          </>
        )}
      </article>
    </MarketingShell>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-widest text-foreground/55 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
